import { useState, useCallback } from 'react'
import { useErrorDialog } from './useErrorDialog'
import { useWindowResize } from './useWindowSize'
import { downloadFile as downloadFileAPI } from '../api/salary'
import {
    createFileBlob,
    shouldDownloadFile,
    downloadFile,
    openFileInBrowser,
    FileInfo,
} from '../utils/fileUtils'

export interface UseFileDownloadReturn {
    isLoading: boolean
    openFile: (file: FileInfo) => Promise<void>
}

/**
 * Hook for handling file downloads with loading state and error handling
 */
export const useFileDownload = (): UseFileDownloadReturn => {
    const [isLoading, setIsLoading] = useState(false)
    const { showError, ErrorDialogComponent } = useErrorDialog()
    const windowSize = useWindowResize()

    const openFile = useCallback(
        async (file: FileInfo): Promise<void> => {
            setIsLoading(true)

            try {
                const extension = file.extension.toLowerCase()
                const response = await downloadFileAPI(
                    file.caseNumber,
                    file.fullName
                )

                if (response.error) {
                    showError(
                        response.error.errors ||
                            'An error occurred while downloading the file'
                    )
                    return
                }

                const blob = createFileBlob(response, extension)
                const isMobile = windowSize?.width
                    ? windowSize.width < 1024
                    : false

                if (shouldDownloadFile(extension)) {
                    // For .msg, .eml files, or unknown file types, trigger download
                    downloadFile(blob, file.fullName)
                } else {
                    // For known file types (pdf, txt, jpg, png), open in browser
                    openFileInBrowser(blob, isMobile)
                }
            } catch (error) {
                console.error('Error downloading file:', error)
                showError('Der skete en fejl - kontakt support')
            } finally {
                setIsLoading(false)
            }
        },
        [showError, windowSize]
    )

    return {
        isLoading,
        openFile,
    }
}

// Export the ErrorDialogComponent for use in components
export { useErrorDialog }
